// Plan Q&A AI Service
// Refactored to use backend API instead of direct OpenAI calls

import axios from 'axios';
import { auth } from '../firebase';
import {
  PlanQaContext,
  PlanQaMessage,
  AiResponseData,
  PlanQaError,
  PlanQaErrorCodes,
  LanguageContext
} from '../types/planQa';

const BACKEND_URL = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001')
  : '';

export class PlanQaAiService {
  private readonly maxTokens = 500;
  private readonly temperature = 0.7;
  private threadCache: Map<string, string> = new Map(); // Cache thread IDs per conversation

  constructor() {
    console.log('✅ PlanQaAiService initialized (using backend API)');
  }

  /**
   * Get Firebase ID token for backend authentication
   */
  private async getAuthToken(): Promise<string> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new PlanQaError(
        'User not authenticated',
        PlanQaErrorCodes.AI_GENERATION_FAILED
      );
    }
    return await currentUser.getIdToken();
  }

  /**
   * Generate AI response for plan-specific question using backend API
   */
  async generatePlanQaResponse(
    userQuestion: string,
    planContext: PlanQaContext,
    conversationHistory: PlanQaMessage[] = []
  ): Promise<AiResponseData> {
    try {
      console.log('🚀 Starting AI response generation via backend:', {
        questionLength: userQuestion.length,
        hasContext: !!planContext,
        contextType: planContext?.contextType,
        historyLength: conversationHistory.length
      });

      const startTime = Date.now();

      // Detect user's language
      const languageContext = this.detectLanguage(userQuestion);
      console.log('🌍 Language detected:', languageContext);

      // Build prompts
      const systemPrompt = this.buildSystemPrompt(planContext, languageContext);
      const userPrompt = this.buildUserPrompt(userQuestion, conversationHistory);

      // Get or create thread
      const conversationId = `${planContext.planId}-${planContext.day}`;
      let threadId = this.threadCache.get(conversationId);

      // Get auth token
      const idToken = await this.getAuthToken();

      if (!threadId) {
        // Create new thread
        console.log('🧵 Creating new thread via backend...');
        const createResponse = await axios.post(
          `${BACKEND_URL}/api/ai/chat`,
          { action: 'create_thread' },
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        threadId = createResponse.data.data.threadId;
        if (threadId) {
          this.threadCache.set(conversationId, threadId);
        }
        console.log('✅ Thread created:', threadId);
      }

      // Send message to assistant
      console.log('📤 Sending message to assistant...');
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        {
          action: 'send_message',
          threadId,
          systemPrompt,
          message: userPrompt,
          language: languageContext.detectedLanguage
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000 // 2 minute timeout for assistant
        }
      );

      const aiMessage = response.data.data.message;
      const responseTime = Date.now() - startTime;

      console.log('✅ AI response received');

      return {
        content: aiMessage.trim(),
        tokensUsed: 0,
        responseTime,
        model: 'gpt-4'
      };

    } catch (error) {
      console.error('❌ Error generating AI response:', error);

      if (error instanceof PlanQaError) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if (status === 429) {
          throw new PlanQaError(
            'Rate limit exceeded. Please try again later.',
            PlanQaErrorCodes.RATE_LIMIT_EXCEEDED,
            error
          );
        }

        if (status === 401) {
          throw new PlanQaError(
            'Authentication failed. Please sign in again.',
            PlanQaErrorCodes.AI_GENERATION_FAILED,
            error
          );
        }

        if (error.message.includes('timeout')) {
          throw new PlanQaError(
            'Request timed out. Please try again.',
            PlanQaErrorCodes.AI_GENERATION_FAILED,
            error
          );
        }
      }

      throw new PlanQaError(
        'Failed to generate AI response',
        PlanQaErrorCodes.AI_GENERATION_FAILED,
        error
      );
    }
  }

  /**
   * Build system prompt with plan context
   */
  private buildSystemPrompt(context: PlanQaContext, languageContext: LanguageContext): string {
    const languageInstructions = this.getLanguageInstructions(languageContext.detectedLanguage);

    return `You are a certified animal behaviorist and pet care expert. ${languageInstructions}

CONTEXT:
- Plan: ${context.planTitle}
- Day: ${context.day}
- Element: ${context.contextElement || 'General question'}
- Description: ${context.planDescription}

Provide specific, actionable advice (under 400 words).`;
  }

  /**
   * Build user prompt
   */
  private buildUserPrompt(question: string, history: PlanQaMessage[]): string {
    let prompt = '';

    if (history.length > 0) {
      const recentContext = history
        .slice(-4)
        .map(msg => `${msg.role}: ${msg.content}`)
        .join('\n');
      prompt += `Recent context:\n${recentContext}\n\n`;
    }

    prompt += `Question: ${question}\n\nProvide a helpful, practical response.`;
    return prompt;
  }

  /**
   * Get language-specific instructions
   */
  private getLanguageInstructions(language: 'zh-CN' | 'es' | 'fr' | 'en'): string {
    const instructions = {
      'zh-CN': 'Respond in Simplified Chinese (简体中文).',
      'es': 'Respond in Spanish.',
      'fr': 'Respond in French.',
      'en': 'Respond in English.'
    };
    return instructions[language] || instructions.en;
  }

  /**
   * Detect language
   */
  private detectLanguage(text: string): LanguageContext {
    if (/[\u4e00-\u9fff]/.test(text)) {
      return { detectedLanguage: 'zh-CN', confidence: 0.9 };
    }
    if (/[áéíóúñü¿¡]/.test(text.toLowerCase())) {
      return { detectedLanguage: 'es', confidence: 0.8 };
    }
    if (/[àâäéèêëïîôöùûüÿç]/.test(text.toLowerCase())) {
      return { detectedLanguage: 'fr', confidence: 0.8 };
    }
    return { detectedLanguage: 'en', confidence: 0.7 };
  }

  /**
   * Generate plan completion summary
   */
  async generatePlanCompletionSummary(
    planContext: PlanQaContext,
    allConversations: PlanQaMessage[],
    planStats: {
      totalDays: number;
      completedActivities: string[];
      averageRating: number;
      totalQuestions: number;
    }
  ): Promise<AiResponseData> {
    try {
      const startTime = Date.now();
      const idToken = await this.getAuthToken();

      const systemPrompt = `You are analyzing a completed pet care journey. Create a warm summary covering: celebration, insights, growth, challenges, and recommendations (500-700 words).

Plan: ${planContext.planTitle}
Days: ${planStats.totalDays}
Questions: ${planStats.totalQuestions}`;

      const userPrompt = `Analyze this journey and provide an encouraging summary.`;

      // Create new thread for summary
      const createResponse = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        { action: 'create_thread' },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const threadId = createResponse.data.data.threadId;

      // Send message
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        {
          action: 'send_message',
          threadId,
          systemPrompt,
          message: userPrompt
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000
        }
      );

      const responseTime = Date.now() - startTime;

      return {
        content: response.data.data.message.trim(),
        tokensUsed: 0,
        responseTime,
        model: 'gpt-4'
      };

    } catch (error) {
      console.error('Error generating summary:', error);
      throw new PlanQaError(
        'Failed to generate plan completion summary',
        PlanQaErrorCodes.AI_GENERATION_FAILED,
        error
      );
    }
  }

  /**
   * Generate follow-up suggestions
   */
  async generateFollowUpSuggestions(
    planContext: PlanQaContext,
    currentConversation: PlanQaMessage[]
  ): Promise<string[]> {
    try {
      const idToken = await this.getAuthToken();

      const systemPrompt = `Suggest 3 relevant follow-up questions for "${planContext.contextElement}" in "${planContext.planTitle}". Return only the questions, one per line.`;

      const conversationSummary = currentConversation
        .slice(-4)
        .map(msg => `${msg.role}: ${msg.content}`)
        .join('\n');

      // Create thread
      const createResponse = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        { action: 'create_thread' },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const threadId = createResponse.data.data.threadId;

      // Send message
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        {
          action: 'send_message',
          threadId,
          systemPrompt,
          message: `Context:\n${conversationSummary}\n\nGenerate 3 follow-up questions:`
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const suggestions = response.data.data.message.trim()
        .split('\n')
        .filter((q: string) => q.trim().length > 0)
        .slice(0, 3);

      return suggestions;

    } catch (error) {
      console.error('Error generating follow-up suggestions:', error);
      return [];
    }
  }
}

// Export singleton instance
export const planQaAiService = new PlanQaAiService();
